<script setup lang="ts">
import { useNotify } from "@/composition/notification";
import { useMethods } from "@/composition/useMethods";
import { FormServices } from "@/service/FormServices";
import { SurveyServices } from "@/service/SurveyServices";
import { useAppStore } from "@/store/app";
import {
  convertSlideJumpToToConditionalLogic,
  convertSlideLogicToConditionalLogic,
  generateFooterStyle,
  getAllFontsImport,
  getAllUniqueFonts,
  fixSafariCursor,
  getCaretOffsetWithin,
  setCaretOffsetWithin,
} from "@/util/methods";
import {
  ArrowLeftIcon,
  Edit01Icon,
  EyeIcon,
  Save01Icon,
  Share07Icon,
} from "@gohighlevel/ghl-icons/24/outline";
import { UIButton, UIToolbar, UIToolbarGroup } from "@gohighlevel/ghl-ui";
import { computed, nextTick, ref, watch } from "vue";

import { LocationService } from "@/service/LocationServices";
import { PaymentServices } from "@/service/PaymentServices";
import { MISMATCH_DATA_TYPE } from "@/util/constants";
import { isCustomObjectField } from "@/util/customObjectUtils";
import { cloneDeep } from "lodash";
import { useI18n } from "vue-i18n";
import { useRouter } from "vue-router";
import { PRODUCT_TYPE as PaymentProductType } from "../../util/productConstants";
import { createFieldCSS, defaultButtonStyle } from "./../../util/default";
import AlertModal from "./AlertModal.vue";
import Integration from "./Integration/Integration.vue";

const { t } = useI18n();
const { sendNotification } = useNotify();
const { sleep } = useMethods();

const store = useAppStore();
const router = useRouter();

const integrateModel = ref(false);
const scriptModalHiddenCheckbox = ref(false);
const title = ref("Alert");
const description = ref("Description");
const positiveText = ref("Ok");
const negativeText = ref("Cancel");
const openAlertModal = ref(false);
const modalName = ref("");
const isSaving = ref(false);
const editHeader = ref(false);
const formNameInput = ref<any>(null);
const containHTMLWhilePreview = ref(false);

// Handler method to call the imported fixSafariCursor function
const handleSafariCursor = (e: Event) => {
  const isKeyDown = e.type === "keydown";
  fixSafariCursor(e, isKeyDown);
};

const normalizedBaseUrl = computed(() => {
  const base: any = store.integrate && store.integrate.baseUrl;
  return Array.isArray(base) ? base[0] ?? "" : base || "";
});

watch(
  () => store.autoSaveFormOrSurvey,
  () => {
    formSaveAndUpdate();
  }
);

watch(
  () => store.triggerBack,
  (newValue) => {
    if (newValue) {
      back();
    }
  }
);

const fieldsMapping = async (formBuilderElements) => {
  const elements = await Promise.all(
    await formBuilderElements.map(async (element) => {
      if (element?.model === "opportunity") {
        return element;
      }
      const currentElement = element;
      let customFieldParams: any = {
        ...(!element.customEdited || element.__pendingClone
          ? { name: element.customFieldLabel || element.label }
          : {}),
        fieldKey: element?.fieldKey,
        dataType: element?.dataType,
        documentType: "field",
        showInForms: true,
        model: "contact",
        parentId: element?.parentId || store.parentFolderId,
        description: "",
      };

      if (element.dataType === "TEXTBOX_LIST") {
        if (isCustomObjectField(element && element.model)) {
          const normalized = (
            Array.isArray(element && element.picklistOptions) ? element.picklistOptions : []
          ).map((opt) => {
            if (typeof opt === "string") {
              return { label: opt, key: opt };
            }
            const label = (opt?.label ?? opt?.key ?? opt?.value ?? "").toString();
            const key = (opt?.key ?? opt?.label ?? opt?.value ?? "").toString();
            return { label, key };
          });
          customFieldParams = {
            ...customFieldParams,
            options: normalized,
          };
        } else {
          const textBoxListOptions = (
            Array.isArray(element && element.picklistOptions) ? element.picklistOptions : []
          ).map((opt) => {
            if (typeof opt === "string") {
              return { label: opt, prefillValue: "" };
            }
            return {
              label: (opt?.label ?? opt?.key ?? opt?.value ?? "").toString(),
              prefillValue: (opt?.prefillValue ?? "").toString(),
            };
          });
          customFieldParams = {
            ...customFieldParams,
            textBoxListOptions,
          };
        }
      }
      if (["SINGLE_OPTIONS", "MULTIPLE_OPTIONS", "CHECKBOX"].includes(element.dataType)) {
        const isCustom = isCustomObjectField(element?.model);
        customFieldParams = {
          ...customFieldParams,
          options: Array.isArray(element?.picklistOptions)
            ? isCustom
              ? element.picklistOptions.map((option: any) => {
                  const label = (
                    typeof option === "string"
                      ? option
                      : option?.label ?? option?.key ?? option?.value ?? ""
                  ).toString();
                  return { label, key: label };
                })
              : (element.picklistOptions.map((option: any) =>
                  typeof option === "object" && option !== null ? option.label : option
                ) as string[])
            : element?.picklistOptions,
        } as any;
      }
      if (element.dataType === "RADIO") {
        const imageOptions = Array.isArray((element as any)?.picklistOptionsImage)
          ? ((element as any).picklistOptionsImage as any[])
          : [];
        const textOptions: string[] | undefined = Array.isArray(element?.picklistOptions)
          ? (element?.picklistOptions.map((option) =>
              typeof option === "object" && option !== null ? option.label : option
            ) as string[])
          : undefined;
        const hasImages = imageOptions.length > 0;
        customFieldParams = {
          ...customFieldParams,
          options: hasImages ? null : textOptions && textOptions.length ? textOptions : [],
          allowCustomOption: element?.allowCustomOption,
          ...(hasImages
            ? {
                picklistOptionsImage: imageOptions.map((opt: any) => ({
                  label: opt?.label,
                  src: opt?.src,
                })),
              }
            : {}),
        };
      }

      if (element.dataType === "FILE_UPLOAD") {
        customFieldParams = {
          ...customFieldParams,
          acceptedFormat:
            element?.picklistOptions?.length === 0 ? ["all"] : element?.picklistOptions,
          isMultipleFile: element?.isMultipleFile,
          maxNumberOfFiles: element?.isMultipleFile ? parseInt(element?.maxFileSize) : undefined,
        };
      }
      if (element.dataType === "SCORE") {
        customFieldParams = {
          ...customFieldParams,
          dataType: "NUMERICAL",
        };
      }
      if (element.dataType === "RATING") {
        customFieldParams = {
          ...customFieldParams,
          dataType: "NUMERICAL",
        };
      }
      try {
        let response;
        // Create once for new or pending cloned custom fields
        if ((element.custom && !element.customEdited) || element.__pendingClone) {
          response = await LocationService.createCustomField(store.locationId, customFieldParams);
        }
        // Only update existing custom fields; never update a pending clone.
        if (
          element.customfieldUpdated &&
          !element.__pendingClone &&
          element?.model !== "business" &&
          !isCustomObjectField(element?.model)
        ) {
          delete customFieldParams.fieldKey;
          response = await LocationService.updateCustomField(
            store.locationId,
            element?.Id || element?.id,
            customFieldParams
          );
        }
        if (response?.status === 201 || response?.status === 200) {
          const apiField = response.data.customField || {};
          const base = {
            ...currentElement,
            ...apiField,
            customEdited: true,
            tag: apiField.id,
            fieldKey: apiField.fieldKey,
            hiddenFieldQueryKey:
              currentElement?.hiddenFieldQueryKey || (apiField.fieldKey || "").split(".")[1],
            multipleFilesAllowed: currentElement?.isMultipleFile,
            multiFileLimit: currentElement?.isMultipleFile
              ? parseInt(currentElement?.maxFileSize)
              : undefined,
            placeholder: currentElement.placeholder,
            dataType: MISMATCH_DATA_TYPE[element.dataType]
              ? MISMATCH_DATA_TYPE[element.dataType]
              : apiField.dataType,
            description: currentElement.description,
            __pendingClone: false,
          } as any;

          // Normalize radio image vs text options so we don't keep both
          if (base?.dataType === "RADIO") {
            const imageOptionsRaw: any[] = Array.isArray(apiField?.picklistOptionsImage)
              ? (apiField.picklistOptionsImage as any[])
              : Array.isArray((apiField as any)?.picklistImageOptions)
              ? ((apiField as any).picklistImageOptions as any[])
              : Array.isArray(base?.picklistOptionsImage)
              ? (base.picklistOptionsImage as any[])
              : [];
            const imageOptions = imageOptionsRaw.map((opt: any) => ({
              label: opt?.label,
              src: opt?.src,
            }));
            const hasImages = imageOptions.length > 0;

            // Exclude text options when images exist
            const {
              picklistOptions: _omitPicklistOptions,
              picklistImageOptions: _omitAltImageOptions,
              ...rest
            } = base;

            return {
              ...rest,
              ...(hasImages
                ? { picklistOptionsImage: imageOptions }
                : {
                    picklistOptions: Array.isArray(base?.picklistOptions)
                      ? base.picklistOptions
                      : [],
                  }),
            };
          }

          return base;
        }
      } catch (error) {
        console.error("Hello", error);
        throw new Error(error?.response?.data?.message || error.message);
      }

      if (element.id !== undefined) {
        return currentElement;
      }
      delete currentElement.icon;
      delete currentElement.category;
      return currentElement;
    })
  );
  return elements;
};

const formSurveyBeforeSaveMapping = async () => {
  try {
    const fieldsOrSlides = store.formId ? store.elements : store.slides;
    const getRequiredFonts = getAllUniqueFonts(
      store.fieldStyle,
      store.submitMessageStyle,
      fieldsOrSlides,
      store.formId,
      store.footerStyle?.fontFamily
    );
    if (validToCreateNewRecurringProduct()) {
      const product = createRecurringDonationProduct();
      const { data: productData } = await PaymentServices.createProduct(product);
      store.builderConfig.recurringProductId = productData._id;
      store.builderConfig.recurringProductCurrency = store.payment.currency.code;
      store.builderConfig.recurringProducts.push({
        currency: store.payment.currency.code,
        recurringProductId: productData._id,
      });
    } else {
      const recurringProduct = store.builderConfig.recurringProducts.find(
        (product) => product.currency === store.payment.currency.code
      );
      if (recurringProduct) {
        store.builderConfig.recurringProductId = recurringProduct.recurringProductId;
        store.builderConfig.recurringProductCurrency = recurringProduct.currency;
      }
    }
    if (store.formId) {
      return formDataToBeSubmitted(getRequiredFonts);
    }
    if (store.surveyId) {
      return surveyDataToBeSubmitted(getRequiredFonts);
    }
    return quizDataToBeSubmitted(getRequiredFonts);
  } catch (error) {
    console.error("Error in formSurveyBeforeSaveMapping", error);
  }
};

const quizDataToBeSubmitted = async (getRequiredFonts) => {
  store.slides = await Promise.all(
    store.slides.map(async (slide) => ({
      ...slide,
      slideData: await fieldsMapping(slide.slideData),
    }))
  );
  store.footerStyle.computedStyles = generateFooterStyle(store, "desktop");
  store.mobileFooterStyle.computedStyles = generateFooterStyle(store, "mobile");
  const dataToBeSubmitted = {
    name: store.builder.name,
    formData: {
      fieldCSS:
        getAllFontsImport(getRequiredFonts, store.builderConfig.isGDPRCompliant) +
          createFieldCSS(store.fieldStyle, store.builderConfig.inputStyleType) || "",
      mobileFieldCSS:
        getAllFontsImport(getRequiredFonts, store.builderConfig.isGDPRCompliant) +
          createFieldCSS(store.fieldStyle, store.builderConfig.inputStyleType, true) || "",
      form: {
        company: {
          domain: store.company.domain,
          logoURL: store.company.logoURL,
          name: store.company.name,
        },
        customStyle: store.formStyle.customStyle || "",
        disableAutoNavigation: store.builderConfig.disableAutoNavigation,
        isBackButtonEnable: store.builderConfig.isBackButtonEnable,
        fbPixelId: store.facebook.fbPixelId || "",
        fieldStyle: store.fieldStyle,
        footerStyle: store.footerStyle,
        mobileFooterStyle: store.mobileFooterStyle,
        formAction: {
          actionType: store.formAction.actionType,
          redirect_url: store.formAction.redirectUrl,
          thankyouText: store.formAction.thankyouText,
          mobileThankyouText: (store.formAction as any).mobileThankyouText,
          footerHtml: store.formAction.footerHtml,
          headerFullWidthEnable: false,
          headerImageSrc: store.formAction.headerImageSrc,
          mobileHeaderImageSrc: store.formAction.mobileHeaderImageSrc,
        },
        isAnimationDisabled: store.builderConfig.isAnimationDisabled,
        formLabelVisible: store.builderConfig.formLabelVisible || false,
        formSubmissionEvent: store.facebook.formSubmissionEvent,
        inputStyleType: store.builderConfig.inputStyleType,
        pageViewEvent: store.facebook.pageViewEvent,
        stickyContact: store.builderConfig.stickyContact,
        isGDPRCompliant: store.builderConfig.isGDPRCompliant,
        enableTimezone: store.builderConfig.enableTimezone,
        currentThemeId: store.builderConfig.currentThemeId,
        style: {
          fieldSpacing: store.formStyle.fieldSpacing,
          ac_branding: store.formStyle.acBranding,
          background: store.formStyle.background,
          bgImage: store.formStyle.bgImage,
          mobileBgImage: store.formStyle.mobileBgImage,
          border: store.formStyle.border,
          padding: store.formStyle.padding,
          mobilePadding: store.formStyle.mobilePadding,
          margin: store.formStyle.margin,
          mobileMargin: store.formStyle.mobileMargin,
          shadow: store.formStyle.shadow,
        },
        submitMessageStyle: store.submitMessageStyle,
        width: store.formStyle.width,
        emailNotificationsConfig: store.emailNotificationsConfig,
        opportunitySettings: store.opportunitySettings,
        contactAssociationSettings: store.contactAssociationSettings,
        autoResponderConfig: store.autoResponderConfig,
      },
      logic: store.logic,
      slides: store.slides,
      category: store.category,
      emailNotifications: store.builderConfig.emailNotifications,
      autoResponder: store.builderConfig.autoResponder,
      resultTemplate: store.resultTemplate,
      categoryCustomFields: await fieldsMapping(store.categoryCustomFields),
      parentFolderId: store.parentFolderId,
      parentFolderName: store.parentFolderName,
      language: localStorage.getItem("locale"),
    },
  };
  return dataToBeSubmitted;
};

const surveyDataToBeSubmitted = async (getRequiredFonts) => {
  store.slides = await Promise.all(
    store.slides.map(async (slide) => ({
      ...slide,
      slideData: await fieldsMapping(slide.slideData),
    }))
  );
  let clonedSlides = cloneDeep(store.slides);
  clonedSlides = await Promise.all(
    clonedSlides.map(async (slide) => {
      const groupAddressIndex = slide.slideData.findIndex(
        (element) => element.tag === "group_address"
      );

      // if group address is present , add store.addressSettings.children after group_address
      if (groupAddressIndex !== -1) {
        slide.slideData.splice(groupAddressIndex + 1, 0, ...store.addressSettings.children);
      }
      return {
        ...slide,
        slideData: await fieldsMapping(slide.slideData),
      };
    })
  );
  store.sidebarOverlaySettings = { ...store.sidebarOverlaySettings };
  clonedSlides.map((value: any, key: any) => {
    value.button = {
      background: defaultButtonStyle.bgColor,
      color: defaultButtonStyle.color,
      border: {
        border: defaultButtonStyle.border,
        radius: defaultButtonStyle.radius,
        padding: defaultButtonStyle.padding,
      },
    };
  });
  if (store.builderConfig.newFooter) {
    store.footerStyle.computedStyles = generateFooterStyle(store, "desktop");
    store.mobileFooterStyle.computedStyles = generateFooterStyle(store, "mobile");
  }

  const dataToBeSubmitted = {
    name: store.builder.name,
    formData: {
      fieldCSS:
        getAllFontsImport(getRequiredFonts, store.builderConfig.isGDPRCompliant) +
          createFieldCSS(store.fieldStyle, store.builderConfig.inputStyleType) || "",
      mobileFieldCSS:
        getAllFontsImport(getRequiredFonts, store.builderConfig.isGDPRCompliant) +
          createFieldCSS(store.fieldStyle, store.builderConfig.inputStyleType, true) || "",
      form: {
        company: {
          domain: store.company.domain,
          logoURL: store.company.logoURL,
          name: store.company.name,
        },
        customStyle: store.formStyle.customStyle || "",
        disableAutoNavigation: store.builderConfig.disableAutoNavigation,
        fbPixelId: store.facebook.fbPixelId || "",
        fieldStyle: store.fieldStyle,
        footerStyle: store.footerStyle,
        mobileFooterStyle: store.mobileFooterStyle,
        address: store.addressSettings,
        formAction: {
          actionType: store.formAction.actionType,
          disqualifiedText: store.formAction.disqualifiedText,
          disqualifiedType: store.formAction.disqualifiedType.toString(),
          disqualifiedUrl: store.formAction.disqualifiedUrl,
          endsurveyText: store.formAction.endsurveyText,
          endsurveyType: store.formAction.endsurveyType.toString(),
          endsurveyUrl: store.formAction.endsurveyUrl,
          fieldPerPage: store.formAction.fieldPerPage,
          fieldSettingEnable: store.formAction.fieldSettingEnable,
          footerHtml: store.formAction.footerHtml,
          headerFullWidthEnable: false,
          headerImageSrc: store.formAction.headerImageSrc,
          mobileHeaderImageSrc: store.formAction.mobileHeaderImageSrc,
          redirect_url: store.formAction.redirectUrl,
          thankyouText: store.formAction.thankyouText,
          mobileThankyouText: (store.formAction as any).mobileThankyouText,
        },
        formLabelVisible: store.builderConfig.formLabelVisible || false,
        formSubmissionEvent: store.facebook.formSubmissionEvent,
        isAnimationDisabled: store.builderConfig.isAnimationDisabled,
        isBackButtonEnable: store.builderConfig.isBackButtonEnable,
        isProgressBarEnabled: store.builderConfig.isProgressBarEnabled,
        isSurveyScrollEnabled: store.builderConfig.isSurveyScrollEnabled,
        inputStyleType: store.builderConfig.inputStyleType,
        pageViewEvent: store.facebook.pageViewEvent,
        stickyContact: store.builderConfig.stickyContact,
        isGDPRCompliant: store.builderConfig.isGDPRCompliant,
        enableTimezone: store.builderConfig.enableTimezone,
        conditionalLogic: store.builderConfig.conditionalLogic,
        currentThemeId: store.builderConfig.currentThemeId,
        style: {
          fieldSpacing: store.formStyle.fieldSpacing,
          ac_branding: store.formStyle.acBranding,
          background: store.formStyle.background,
          bgImage: store.formStyle.bgImage,
          mobileBgImage: store.formStyle.mobileBgImage,
          border: store.formStyle.border,
          padding: store.formStyle.padding,
          mobilePadding: store.formStyle.mobilePadding,
          margin: store.formStyle.margin,
          mobileMargin: store.formStyle.mobileMargin,
          shadow: store.formStyle.shadow,
        },
        submitMessageStyle: store.submitMessageStyle,
        width: store.formStyle.width,
        emailNotificationsConfig: store.emailNotificationsConfig,
        opportunitySettings: store.opportunitySettings,
        contactAssociationSettings: store.contactAssociationSettings,
        autoResponderConfig: store.autoResponderConfig,
        payment: clonedSlides.filter(
          (slide) => slide?.slideData?.filter((element) => element.tag === "payment").length
        ).length
          ? paymentAndProductDetailsMap()
          : null,
      },
      slides: clonedSlides,
      surveyLogicLinkById: store.builderConfig.surveyLogicLinkById,
      newFooter: store.builderConfig.newFooter,
      enablePartialContactCreation: store.builderConfig.enablePartialContactCreation,
      emailNotifications: store.builderConfig.emailNotifications,
      autoResponder: store.builderConfig.autoResponder,
      parentFolderId: store.parentFolderId,
      parentFolderName: store.parentFolderName,
      recurringProductId: store.builderConfig.recurringProductId,
      recurringProductCurrency: store.builderConfig.recurringProductCurrency,
      recurringProducts: store.builderConfig.recurringProducts,
      language: localStorage.getItem("locale"),
    },
  };

  // Prepare conditional logic payload (merge slide.logic migration + slide.jumpTo)
  if (store.surveyId) {
    const existing = store.builderConfig.conditionalLogic || [];
    let migrated = existing;
    if (!existing.length && store.builder.version >= 4) {
      const fromSlideLogic = convertSlideLogicToConditionalLogic(store.slides, store);
      migrated = Array.isArray(fromSlideLogic) ? fromSlideLogic : [];
    }
    const withJumpTo = convertSlideJumpToToConditionalLogic(store.slides, migrated);
    if (withJumpTo.length) {
      dataToBeSubmitted.formData.form.conditionalLogic = withJumpTo;
      store.builderConfig.conditionalLogic = withJumpTo;
    }
  }

  return dataToBeSubmitted;
};

const formDataToBeSubmitted = async (getRequiredFonts) => {
  const height = document?.getElementById("form-builder-container")?.offsetHeight;
  store.formStyle.height = height;
  store.elements = await fieldsMapping(store.elements);
  // adding to support the places where these address fields are used from this fields array
  let clonedFields = cloneDeep(store.elements);
  const groupAddressIndex = clonedFields.findIndex((element) => element.tag === "group_address");
  if (groupAddressIndex !== -1) {
    clonedFields.splice(groupAddressIndex + 1, 0, ...store.addressSettings.children);
  }
  clonedFields = await fieldsMapping(clonedFields);
  const dataToBeSubmitted = {
    name: store.builder.name,
    formData: {
      form: {
        button: store.builder.formData?.form?.button ? null : undefined,
        company: {
          domain: store.company.domain,
          logoURL: store.company.logoURL,
          name: store.company.name,
        },
        customStyle: store.formStyle.customStyle || "",
        fieldCSS:
          getAllFontsImport(getRequiredFonts, store.builderConfig.isGDPRCompliant) +
            createFieldCSS(store.fieldStyle, store.builderConfig.inputStyleType) || "",
        mobileFieldCSS:
          getAllFontsImport(getRequiredFonts, store.builderConfig.isGDPRCompliant) +
            createFieldCSS(store.fieldStyle, store.builderConfig.inputStyleType, true) || "",
        fbPixelId: store.facebook.fbPixelId || "",
        fieldStyle: store.fieldStyle,
        fields: clonedFields,
        payment: clonedFields.filter((element) => element.tag === "payment").length
          ? paymentAndProductDetailsMap()
          : null,
        address: store.addressSettings,
        formAction: {
          actionType: store.formAction.actionType,
          redirect_url: store.formAction.redirectUrl,
          thankyouText: store.formAction.thankyouText,
          mobileThankyouText: (store.formAction as any).mobileThankyouText,
          headerImageSrc: store.formAction.headerImageSrc,
          mobileHeaderImageSrc: store.formAction.mobileHeaderImageSrc,
        },
        formLabelVisible: store.builderConfig.formLabelVisible || false,
        formSubmissionEvent: store.facebook.formSubmissionEvent,
        height: store.formStyle.height,
        layout: store.builderConfig.layout,
        mobileLayout: store.builderConfig.mobileLayout,
        inputStyleType: store.builderConfig.inputStyleType,
        pageViewEvent: store.facebook.pageViewEvent,
        stickyContact: store.builderConfig.stickyContact,
        isGDPRCompliant: store.builderConfig.isGDPRCompliant,
        enableTimezone: store.builderConfig.enableTimezone,
        conditionalLogic: store.builderConfig.conditionalLogic,
        fullScreenMode: store.builderConfig.fullScreenMode,
        currentThemeId: store.builderConfig.currentThemeId,
        style: {
          fieldSpacing: store.formStyle.fieldSpacing,
          ac_branding: store.formStyle.acBranding,
          bgImage: store.formStyle.bgImage,
          mobileBgImage: store.formStyle.mobileBgImage,
          background: store.formStyle.background,
          border: store.formStyle.border,
          padding: store.formStyle.padding,
          mobilePadding: store.formStyle.mobilePadding,
          margin: store.formStyle.margin,
          mobileMargin: store.formStyle.mobileMargin,
          shadow: store.formStyle.shadow,
        },
        submitMessageStyle: store.submitMessageStyle,
        width: store.formStyle.width,
        emailNotificationsConfig: store.emailNotificationsConfig,
        opportunitySettings: store.opportunitySettings,
        contactAssociationSettings: store.contactAssociationSettings,
        autoResponderConfig: store.autoResponderConfig,
      },
      recurringProductId: store.builderConfig.recurringProductId,
      recurringProductCurrency: store.builderConfig.recurringProductCurrency,
      recurringProducts: store.builderConfig.recurringProducts,
      emailNotifications: store.builderConfig.emailNotifications,
      autoResponder: store.builderConfig.autoResponder,
      parentFolderId: store.parentFolderId,
      parentFolderName: store.parentFolderName,
      language: localStorage.getItem("locale"),
    },
  };
  return dataToBeSubmitted;
};
const formSaveAndUpdate = async () => {
  try {
    isSaving.value = true;
    const dataToBeSubmitted = await formSurveyBeforeSaveMapping();
    store.anyUnsavedChanges = false;
    if (store.formId || store.quizId) {
      const builderId = store.formId || store.quizId;
      const { data: formData } = await FormServices.updateFormById(builderId, dataToBeSubmitted);
      store.builder.versionHistory = formData.data.versionHistory;
      if (store.quizId) {
        store.setCategoryCustomFields(formData.data?.formData?.categoryCustomFields);
      }
    } else {
      const { data: surveyData } = await SurveyServices.updateFormById(
        store.surveyId,
        dataToBeSubmitted
      );
      store.builder.versionHistory = surveyData.data.versionHistory;
    }
    await sleep(2000);
    isSaving.value = false;
    await store.getCustomFieldFolders();
    await store.getCustomFields();
  } catch (err) {
    store.anyUnsavedChanges = true;
    isSaving.value = false;
    sendNotification({ title: "Error, Retry again", description: err.message, type: "error" });
  }
};

const createRecurringDonationProduct = () => {
  return {
    name: store.builder.name + " - Recurring Donation",
    locationId: store.locationId,
    productType: "DIGITAL",
    availableInStore: true,
    seo: {
      title: "",
      description: "",
    },
    medias: [],
    variants: [],
    isTaxesEnabled: false,
    isLabelEnabled: false,
    taxInclusive: false,
    prices: [
      {
        name: store.builder.name + " - Recurring Donation @ 1 day",
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "day",
          intervalCount: 1,
        },
      },
      {
        name: store.builder.name + " - Recurring Donation @ 1 weekly",
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "week",
          intervalCount: 1,
        },
      },
      {
        name: store.builder.name + " - Recurring Donation @ 1 month",
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "month",
          intervalCount: 1,
        },
      },
      {
        name: store.builder.name + " - Recurring Donation @ 1 year",
        type: "recurring",
        currency: store.payment.currency.code,
        amount: 1,
        recurring: {
          interval: "year",
          intervalCount: 1,
        },
      },
    ],
    meta: {
      internalSource: "recurring_donation",
    },
  };
};

const validToCreateNewRecurringProduct = () => {
  return (
    (store.payment?.recurringDonation?.isRecurringDonationEnabled &&
      !store.builderConfig.recurringProductId) ||
    (store.payment?.recurringDonation?.isRecurringDonationEnabled &&
      !store.builderConfig.recurringProducts?.some(
        (product) => product.currency === store.payment.currency.code
      ))
  );
};
const back = () => {
  if (store.anyUnsavedChanges) {
    setupAlertModalForBack();
  } else if (store.isModal && store.appHandshake) {
    const id = store.formId || store.surveyId || store.quizId;
    store.appHandshake.then((parent) =>
      parent.emit("modalClose", { id, name: store.builder.name })
    );
  } else {
    router.go(-1);
  }
};

const openPreview = async () => {
  if (containHTML() && !JSON.parse(localStorage.getItem("script_modal_hidden"))) {
    containHTMLWhilePreview.value = true;
    setupAlertModalForScript();
  } else if (store.anyUnsavedChanges) {
    await formSaveAndUpdate();
    redirectToPreview();
  } else {
    redirectToPreview();
  }
};

const redirectToPreview = () => {
  if (store.formId) {
    window
      .open(`${store.previewDomain}/widget/form/${store.formId}?notrack=true`, "_blank")
      .focus();
  } else if (store.surveyId) {
    window
      .open(`${store.previewDomain}/widget/survey/${store.surveyId}?notrack=true`, "_blank")
      .focus();
  } else {
    window
      .open(`${store.previewDomain}/widget/quiz/${store.quizId}?notrack=true`, "_blank")
      .focus();
  }
};

const goBackToMain = () => {
  if (store.isModal && store.appHandshake) {
    const id = store.formId || store.surveyId || store.quizId;
    store.appHandshake.then((parent) =>
      parent.emit("modalClose", { id, name: store.builder.name })
    );
  } else {
    router.go(-1);
  }
};

const openIntegrateFormAndSave = async () => {
  integrateModel.value = true;
  if (containHTML()) {
    setupAlertModalForScript();
  } else if (store.anyUnsavedChanges) {
    await formSaveAndUpdate();
  }
};

const updateCheckBox = (isChecked: boolean) => {
  scriptModalHiddenCheckbox.value = isChecked;
};

const feedback = async (modalName, result) => {
  localStorage.setItem("script_modal_hidden", scriptModalHiddenCheckbox.value.toString());
  if (modalName === "BACK" && result) {
    goBackToMain();
  }
  if (modalName === "SCRIPT" && result) {
    await formSaveAndUpdate();
    if (containHTMLWhilePreview.value) {
      containHTMLWhilePreview.value = false;
      redirectToPreview();
    }
  }
  openAlertModal.value = false;
};

const containHTML = () => {
  if (store.formId) {
    return store.elements.some((element) => element.tag === "html");
  }
  let containAnyHTMLInSurvey = false;
  store.slides.forEach((slide) => {
    if (slide.slideData.some((element) => element.tag === "html")) {
      containAnyHTMLInSurvey = true;
    }
  });
  return containAnyHTMLInSurvey;
};

const integrationAction = (result) => {
  integrateModel.value = result;
};

const popModalChange = () => {
  openAlertModal.value = false;
};

const saveFormOrUpdate = async () => {
  if (containHTML()) {
    setupAlertModalForScript();
  } else if (!containHTML()) {
    await formSaveAndUpdate();
  }
};

const setupAlertModalForScript = () => {
  title.value = `${t("common.alert")}!`;
  description.value = store.formId
    ? `${t("form.thirdPartyScriptAlertMessage")}`
    : `${t("survey.thirdPartyScriptAlertMessage")}`;
  positiveText.value = `${t("common.proceed")}`;
  negativeText.value = `${t("common.cancel")}`;
  modalName.value = "SCRIPT";
  openAlertModal.value = !JSON.parse(localStorage.getItem("script_modal_hidden"));
  if (!openAlertModal.value) {
    formSaveAndUpdate();
  }
};
const setupAlertModalForBack = () => {
  modalName.value = "BACK";
  title.value = `${t("common.alert")}!`;
  description.value = store.formId
    ? `${t("form.unsavedAlertMessage")}`
    : `${t("survey.unsavedAlertMessage")}`;
  positiveText.value = `${t("common.proceed")}`;
  negativeText.value = `${t("common.cancel")}`;
  openAlertModal.value = true;
};
const updateName = (e: Event) => {
  const target = e.target as HTMLElement;
  const caret = getCaretOffsetWithin(target);
  const text = (target.textContent || "").replace(/\u200B/g, "");
  store.builder.name = text;
  store.anyUnsavedChanges = true;
  handleSafariCursor(e);
  nextTick(() => setCaretOffsetWithin(target, caret));
};
const handleEnterPress = (e: KeyboardEvent) => {
  handleSafariCursor(e as unknown as Event);
  if (e.key === "Enter" && formNameInput.value) {
    formNameInput.value?.blur();
  }
};

const paymentAndProductDetailsMap = () => {
  const products = store.addedProductList.map((product) => {
    return {
      productId: product.productId,
      showDescription: product.showDescription,
      showImage: product.showImage,
      showQuantity: product.showQuantity,
    };
  });
  const bumpProducts = store.bumProductList.map((product) => {
    return {
      productId: product.productId,
      priceId: product.priceId,
      headline: product.headline || "",
      otoHeadline: product.otoHeadline || "",
      otoText: product.otoText || "",
      enableImage: product.enableImage || false,
      uploadedImage: product.uploadedImage,
      type: PaymentProductType.BUMP_PRODUCT.value,
    };
  });
  store.payment.products = products;
  store.payment.bumpProducts = bumpProducts;
  return store.payment;
};
</script>
<template>
  <div id="hl-builder-header">
    <UIToolbar class="header-toolbar p-0">
      <UIToolbarGroup class="min-w-[360px]">
        <div class="flex">
          <div v-if="!store.isModal" class="border-right p-3">
            <div class="flex pr-3">
              <UIButton id="builder-back" text size="small" class="text-gray-800" @click="back">
                <ArrowLeftIcon class="mr-2 h-4 w-4" />
                <span>{{ t("common.back") }}</span>
              </UIButton>
            </div>
          </div>
        </div>
      </UIToolbarGroup>
      <UIToolbarGroup>
        <div class="builder-form-name flex items-center justify-center text-black">
          <div
            ref="formNameInput"
            :contenteditable="true"
            :class="`min-w-[1rem] max-w-2xl text-lg outline-none ${!editHeader ? 'truncate' : ''}`"
            @input="updateName"
            @click="editHeader = true"
            @focusout="
              editHeader = false;
              store.addToHistory();
            "
            @keydown="handleEnterPress"
          >
            {{ store.builder.name }}
          </div>
          <Edit01Icon
            v-if="!editHeader"
            class="ml-1 h-4 w-4 text-gray-700 hover:text-gray-500"
            @click="
              editHeader = true;
              formNameInput.focus();
            "
          />
        </div>
      </UIToolbarGroup>

      <UIToolbarGroup class="min-w-[360px]">
        <div class="flex items-center justify-end px-3 py-2">
          <!-- <div
            v-if="store.quizId"
            class="preview-border border-1 flex items-center justify-center rounded-lg p-[10px] px-2"
            @click="openPreview"
          >
            <EyeIcon class="h-4 w-4"></EyeIcon>
          </div> -->
          <UIButton id="integrate-btn" size="small" class="mr-2 text-gray-800" @click="openPreview">
            <EyeIcon class="h-4 w-4"></EyeIcon>
            <div class="ml-2">{{ t("common.preview") }}</div>
          </UIButton>
          <UIButton
            v-if="!store.isModal"
            id="integrate-btn"
            size="small"
            class="mr-2 text-gray-800"
            @click="openIntegrateFormAndSave"
          >
            <Share07Icon class="h-4 w-4"></Share07Icon>
            <div class="ml-2">{{ t("common.integrate") }}</div>
          </UIButton>
          <UIButton
            id="save-action"
            size="small"
            type="primary"
            :disabled="isSaving || !(store?.enableSaveButton ?? true)"
            @click="saveFormOrUpdate"
          >
            <Save01Icon class="mr-2 h-4 w-4"></Save01Icon>
            <span v-if="!isSaving">{{ t("common.save") }}</span>
            <span v-else>{{ t("common.saving") }}</span>
            <span
              v-if="store.anyUnsavedChanges"
              class="absolute right-0 top-0 z-10 -mr-1 -mt-1 flex h-3 w-3"
            >
              <span
                class="absolute inline-flex h-full w-full animate-ping rounded-full bg-amber-400 opacity-70"
              ></span>
              <span class="relative inline-flex h-3 w-3 rounded-full bg-amber-400"></span>
            </span>
          </UIButton>
        </div>
      </UIToolbarGroup>
    </UIToolbar>
    <AlertModal
      v-if="openAlertModal"
      :title="title"
      :description="description"
      :modal-name="modalName"
      :show-modal-popup="openAlertModal"
      :positive-text="positiveText"
      :negative-text="negativeText"
      :is-checkbox-enabled="true"
      :hide-modal="scriptModalHiddenCheckbox"
      header-type="warning"
      footer-type="primary"
      @pop:change="popModalChange"
      @pop:positive="feedback"
      @pop:negative="feedback"
      @pop:check="updateCheckBox"
    ></AlertModal>
    <Integration
      v-if="integrateModel"
      :model-active="integrateModel"
      :form-style="store.formStyle"
      :form-name="store.builder.name"
      :base-url="String(store.integrate.baseUrl || '')"
      :is-white-labelled="store.integrate.isWhiteLabelled"
      :form-layout-settings="store.integrate.formLayoutSettings"
      @update:show-model="integrationAction"
    >
    </Integration>
  </div>
</template>

<style scoped>
.header-toolbar {
  background-color: #ffffff !important;
  border-bottom: 0.5px solid #e2e2e2 !important;
}

.builder-form-name {
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  text-align: center;
}

#hl-builder-header .hl-toolbar-group {
  padding: 5px 0px;
}

.form-save-btn {
  width: 100%;
}

#hl-builder-header {
  position: fixed;
  z-index: 21;
  width: 100%;
}

.border-right {
  border-right: 1px solid #dee2e6 !important;
}

.hl-toolbar-group {
  padding: 0px !important;
}

.preview-border {
  border: 1 solid #eff4ff;
  background-color: #eff4ff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
.result-button {
  border-radius: 8px;
  border: 1px solid #d0d5dd;
  background: #fff;
  box-shadow: 0px 1px 2px 0px rgba(16, 24, 40, 0.05);
}
</style>
